import React, { useCallback, useMemo, useState, useEffect } from "react";
import { createEditor } from "slate";
import { withHistory } from "slate-history";
import { Slate, Editable, withReact } from "slate-react";
import Toolbar from "./Toolbar/Toolbar";
import { getMarked, getBlock } from "./utils/SlateUtilityFunctions.js";
import withLinks from "./plugins/withLinks.js";
import withTables from "./plugins/withTable.js";
import withEmbeds from "./plugins/withEmbeds.js";
import withEquation from "./plugins/withEquation.js";
import "./Editor.css";
import CodeToText from "./Elements/CodeToText/CodeToText";
import { deleteImage } from "../../utils/FireStorageFunctions.js";
import { collection, query, where, getDocs, setDoc, serverTimestamp } from "firebase/firestore";
import { useParams } from "react-router-dom";
import { db } from "../../firebase/firebase"; // Dein Firestore-Setup
import { getAuth } from "firebase/auth";

const Element = (props) => {
  return getBlock(props);
};

const Leaf = ({ attributes, children, leaf }) => {
  children = getMarked(leaf, children);
  return <span {...attributes}>{children}</span>;
};

const SlateEditor = () => {
  const { link } = useParams();
  const editor = useMemo(
    () =>
      withEquation(
        withHistory(withEmbeds(withTables(withLinks(withReact(createEditor())))))
      ),
    []
  );
  const [value, setValue] = useState([
    {
      type: "paragraph",
      children: [{ text: "" }],
    },
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false); // State für den Spinner beim Speichern

  const handleEditorChange = (newValue) => {
    setValue(newValue);
  };

  const renderElement = useCallback((props) => <Element {...props} />, []);

  const renderLeaf = useCallback((props) => {
    return <Leaf {...props} />;
  }, []);

  const [htmlAction, setHtmlAction] = useState({
    showInput: false,
    html: "",
    action: "",
    location: "",
  });

  const handleCodeToText = (partialState) => {
    setHtmlAction((prev) => ({
      ...prev,
      ...partialState,
    }));
  };

  const fetchPageByLink = async (link) => {
    const pagesRef = collection(db, "pages");
    const q = query(pagesRef, where("link", "==", link));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("Seite nicht gefunden.");
    }

    return querySnapshot.docs[0];
  };

  // Speichern des Slate-Inhalts
  const handleSave = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      alert("Du musst angemeldet sein, um Änderungen zu speichern.");
      return;
    }

    setSaving(true); // Spinner aktivieren

    try {
      const pageDoc = await fetchPageByLink(link);

      if (!pageDoc) {
        alert("Seite nicht gefunden.");
        setSaving(false);
        return;
      }

      const data = pageDoc.data();
      const pageRef = pageDoc.ref;

      // Lösche das Bild, wenn es vorhanden ist
      if (data.content.image) {
        await deleteImage(data.content.image);
      }

      const jsonValue = JSON.stringify(value);

      await setDoc(
        pageRef,
        {
          lastModifiedBy: currentUser.uid,
          contentType: "text",
          content: { text: jsonValue },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      alert("Seite erfolgreich gespeichert!");
    } catch (err) {
      console.error("Fehler beim Speichern:", err);
      alert("Fehler beim Speichern der Seite.");
    } finally {
      setSaving(false); // Spinner wieder deaktivieren
    }
  };

  // Laden des Slate-Inhalts
  useEffect(() => {
    const loadPage = async () => {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        console.error("Kein Nutzer angemeldet.");
        setValue([
          { type: "paragraph", children: [{ text: "Fehler: Kein Nutzer angemeldet." }] },
        ]);
        setLoading(false);
        return;
      }

      try {
        const pageDoc = await fetchPageByLink(link);

        if (!pageDoc) {
          console.error("Seite nicht gefunden.");
          setValue([
            { type: "paragraph", children: [{ text: "Seite nicht gefunden." }] },
          ]);
          setLoading(false);
          return;
        }

        const data = pageDoc.data();
        const loadedValue = JSON.parse(data.content.text);
        setValue(loadedValue || [{ type: "paragraph", children: [{ text: "" }] }]);
      } catch (err) {
        console.error("Fehler beim Laden:", err);
        setValue([{ type: "paragraph", children: [{ text: "" }] }]);
      }
      setLoading(false);
    };

    loadPage();
  }, [link]);

  if (loading) {
    return <p>Lädt...</p>;
  }

  return (
    <>
      <div>
        <button 
          onClick={handleSave} 
          style={{ marginTop: "10px" }} 
          className="btn btn-primary"
          disabled={saving} // Deaktiviert den Button während des Speicherns
        >
          {saving ? ( 
            <>
              <span 
                className="spinner-border spinner-border-sm" 
                role="status" 
                aria-hidden="true"
              ></span>
              {" "}Speichern...
            </>
          ) : (
            "Speichern"
          )}
        </button>
      </div>
      <Slate editor={editor} value={value} initialValue={value} onChange={handleEditorChange}>
        <Toolbar handleCodeToText={handleCodeToText} />
        <div className="editor-wrapper" style={{ border: "1px solid #f3f3f3", padding: "0 10px" }}>
          <Editable
            placeholder="Write something"
            renderElement={renderElement}
            renderLeaf={renderLeaf}
          />
        </div>
        {htmlAction.showInput && (
          <CodeToText {...htmlAction} handleCodeToText={handleCodeToText} />
        )}
      </Slate>
    </>
  );
};

export default SlateEditor;
